import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { auth, db } from "../services/firebase"
import "./Register.css"

const googleProvider = new GoogleAuthProvider()

function traducirError(code) {
  const errores = {
    "auth/email-already-in-use": "Ya existe una cuenta con ese email.",
    "auth/invalid-email":        "El email no es válido.",
    "auth/weak-password":        "La contraseña es muy débil. Usá al menos 6 caracteres.",
    "auth/popup-closed-by-user": "Cerraste la ventana de Google. Intentá de nuevo.",
  }
  return errores[code] || "Ocurrió un error. Intentá de nuevo."
}

// Ícono ojo
function OjoIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}

// Ícono Google
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

// Indicador de fuerza de contraseña
function FuerzaPassword({ password }) {
  if (!password) return null
  const len     = password.length
  const tieneNum = /\d/.test(password)
  const tieneMay = /[A-Z]/.test(password)

  let nivel = 0
  if (len >= 6)  nivel++
  if (len >= 10) nivel++
  if (tieneNum)  nivel++
  if (tieneMay)  nivel++

  const config = [
    null,
    { label: "Débil",    color: "#ef4444" },
    { label: "Regular",  color: "#f97316" },
    { label: "Buena",    color: "#eab308" },
    { label: "Fuerte",   color: "#22c55e" },
  ]
  const c = config[nivel] || config[1]

  return (
    <div className="pass-fuerza">
      <div className="pass-fuerza__barras">
        {[1,2,3,4].map(i => (
          <div
            key={i}
            className="pass-fuerza__barra"
            style={{ background: i <= nivel ? c.color : "#e8e4dc" }}
          />
        ))}
      </div>
      <span className="pass-fuerza__label" style={{ color: c.color }}>{c.label}</span>
    </div>
  )
}

export default function Register() {
  const [nombre,   setNombre]   = useState("")
  const [apellido, setApellido] = useState("")
  const [email,    setEmail]    = useState("")
  const [password, setPassword] = useState("")
  const [confirmar,setConfirmar]= useState("")
  const [verPass,  setVerPass]  = useState(false)
  const [verConf,  setVerConf]  = useState(false)
  const [error,    setError]    = useState("")
  const [loading,  setLoading]  = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const navigate = useNavigate()

  async function handleSubmit(e) {
    e.preventDefault()
    setError("")

    if (password !== confirmar) return setError("Las contraseñas no coinciden.")
    if (password.length < 6)    return setError("La contraseña debe tener al menos 6 caracteres.")

    setLoading(true)
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      await setDoc(doc(db, "usuarios_web", cred.user.uid), {
        email,
        nombre,
        apellido,
        es_admin:            false,
        id_cliente_asociado: null,
        creado_en:           new Date(),
        proveedor:           "email",
      })
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "CompleteRegistration", { content_name: "registro_web" })
      }
      navigate("/dashboard")   // ← Bug fix: faltaba esta línea
    } catch (err) {
      setError(traducirError(err.code))
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogle() {
    setError("")
    setLoadingGoogle(true)
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      const ref  = doc(db, "usuarios_web", user.uid)
      const snap = await getDoc(ref)
      if (!snap.exists()) {
        const [nom, ...resto] = (user.displayName || "").split(" ")
        await setDoc(ref, {
          email:               user.email,
          nombre:              nom || "",
          apellido:            resto.join(" ") || "",
          es_admin:            false,
          id_cliente_asociado: null,
          creado_en:           new Date(),
          proveedor:           "google",
        })
        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("track", "CompleteRegistration", { content_name: "registro_google" })
        }
      }
      navigate("/dashboard")
    } catch (err) {
      setError(traducirError(err.code))
    } finally {
      setLoadingGoogle(false)
    }
  }

  const coinciden = confirmar.length > 0 && password === confirmar
  const noCoinciden = confirmar.length > 0 && password !== confirmar

  return (
    <div className="register-page">
      <div className="register-card">
        <div className="register-card__icono">🌍</div>
        <h1 className="register-card__titulo">Crear cuenta</h1>
        <p className="register-card__sub">Registrate y accedé a tu panel de viajes</p>

        {/* Botón Google */}
        <button
          className="auth-btn-google"
          onClick={handleGoogle}
          disabled={loadingGoogle || loading}
          type="button"
        >
          <GoogleIcon />
          {loadingGoogle ? "Conectando..." : "Registrarme con Google"}
        </button>

        <div className="auth-divider">
          <span>o completá el formulario</span>
        </div>

        <form className="register-form" onSubmit={handleSubmit}>

          <div className="register-form__row">
            <div className="register-form__group">
              <label className="register-form__label">Nombre</label>
              <input className="register-form__input" type="text" placeholder="Juan"
                value={nombre} onChange={e => setNombre(e.target.value)} required />
            </div>
            <div className="register-form__group">
              <label className="register-form__label">Apellido</label>
              <input className="register-form__input" type="text" placeholder="Pérez"
                value={apellido} onChange={e => setApellido(e.target.value)} required />
            </div>
          </div>

          <div className="register-form__group">
            <label className="register-form__label">Email</label>
            <input className="register-form__input" type="email" placeholder="tu@email.com"
              value={email} onChange={e => setEmail(e.target.value)} required />
          </div>

          <div className="register-form__group">
            <label className="register-form__label">Contraseña</label>
            <div className="input-pass-wrap">
              <input
                className="register-form__input"
                type={verPass ? "text" : "password"}
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="input-pass-ojo"
                onClick={() => setVerPass(v => !v)}
                tabIndex={-1}
                title={verPass ? "Ocultar contraseña" : "Ver contraseña"}
              >
                <OjoIcon visible={verPass} />
              </button>
            </div>
            <FuerzaPassword password={password} />
          </div>

          <div className="register-form__group">
            <label className="register-form__label">Confirmar contraseña</label>
            <div className={"input-pass-wrap" + (noCoinciden ? " input-pass-wrap--error" : coinciden ? " input-pass-wrap--ok" : "")}>
              <input
                className="register-form__input"
                type={verConf ? "text" : "password"}
                placeholder="Repetir contraseña"
                value={confirmar}
                onChange={e => setConfirmar(e.target.value)}
                required
              />
              <button
                type="button"
                className="input-pass-ojo"
                onClick={() => setVerConf(v => !v)}
                tabIndex={-1}
                title={verConf ? "Ocultar contraseña" : "Ver contraseña"}
              >
                <OjoIcon visible={verConf} />
              </button>
            </div>
            {noCoinciden && <span className="register-form__hint register-form__hint--err">⚠️ Las contraseñas no coinciden</span>}
            {coinciden   && <span className="register-form__hint register-form__hint--ok">✓ Las contraseñas coinciden</span>}
          </div>

          {error && <div className="register-form__error">{error}</div>}

          <button className="register-form__btn" type="submit" disabled={loading || loadingGoogle}>
            {loading ? "Creando cuenta..." : "Crear cuenta"}
          </button>
        </form>

        <div className="register-card__footer">
          ¿Ya tenés cuenta?{" "}
          <Link to="/login" className="register-card__link">Iniciá sesión</Link>
        </div>
        <div style={{ textAlign: "center", marginTop: "8px" }}>
          <Link to="/" className="register-card__link" style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  )
}